import { canonicalize, formatScopeKey } from './rbac-contracts.js';
import type {
  AccessRestriction,
  ControlEnvelope,
  PermissionGrant,
  PublicKeyResolver,
  ReplayRecord,
  SignatureVerifier,
  SignedControlPayload,
  SystemRole,
} from './rbac-contracts.js';

export interface JournalDependencies {
  signatureVerifier: SignatureVerifier;
  keyResolver: PublicKeyResolver;
  meshId: string;
}

export class ControlPlaneJournal {
  private policyVersion = 1;
  private readonly replayRegistry = new Map<string, ReplayRecord>();
  private readonly envelopes: ControlEnvelope<SignedControlPayload>[] = [];
  private readonly restrictions = new Map<string, AccessRestriction>();
  private readonly grants: PermissionGrant[] = [];
  private readonly roleAssignments = new Map<string, Map<string, Set<SystemRole>>>();
  private readonly commitListeners: Array<(version: number) => void> = [];

  constructor(private readonly deps: JournalDependencies) {}

  getPolicyVersion(): number {
    return this.policyVersion;
  }

  onEnvelopeCommitted(listener: (version: number) => void): () => void {
    this.commitListeners.push(listener);
    return () => {
      const idx = this.commitListeners.indexOf(listener);
      if (idx >= 0) this.commitListeners.splice(idx, 1);
    };
  }

  appendEnvelope(
    envelope: ControlEnvelope<SignedControlPayload>,
    nowMs: number
  ): { ok: true; policyVersion: number } | { ok: false; error: string } {
    const payload = envelope.payload;

    // 1. Domain & Mesh Isolation Check
    if (payload.domain !== 'mesh-control-plane/v1') {
      return { ok: false, error: 'Invalid domain separation string' };
    }
    if (payload.meshId !== this.deps.meshId) {
      return { ok: false, error: `Mesh ID mismatch: expected ${this.deps.meshId}, got ${payload.meshId}` };
    }

    // 2. Expiration Check
    if (nowMs > payload.expiresAt) {
      return { ok: false, error: 'Control envelope has expired' };
    }

    // 3. Anti-Replay Nonce Validation (Key: meshId | actorId | keyId | nonce)
    const replayKey = `${payload.meshId}|${payload.actorId}|${payload.keyId}|${payload.nonce}`;
    const existingReplay = this.replayRegistry.get(replayKey);
    if (existingReplay && nowMs <= existingReplay.expiresAt) {
      return { ok: false, error: 'Replay detected: nonce has already been used within validity window' };
    }

    // 4. Principal & Key Status Check
    if (this.deps.keyResolver.isKeyRevoked(payload.actorId, nowMs)) {
      return { ok: false, error: 'Actor key or principal has been revoked' };
    }

    // 5. Signature Verification over Canonical JCS String
    const canonicalString = canonicalize(payload);
    const validSig = this.deps.signatureVerifier.verifySignature(
      payload.actorId,
      payload.keyId,
      canonicalString,
      envelope.signature
    );
    if (!validSig) {
      return { ok: false, error: 'Cryptographic signature verification failed' };
    }

    // 6. Optimistic Concurrency Control
    if (payload.expectedPolicyVersion !== this.policyVersion) {
      return {
        ok: false,
        error: `Optimistic concurrency conflict: expected policyVersion ${this.policyVersion}, got ${payload.expectedPolicyVersion}`,
      };
    }

    // 7. Apply State Transition
    const scopeKey = formatScopeKey(payload.scope);
    const envelopeId = `env-${this.policyVersion}-${payload.nonce}`;

    switch (payload.action) {
      case 'ASSIGN_ROLE': {
        if (!payload.role) return { ok: false, error: 'Role required for ASSIGN_ROLE' };
        let subjectRoles = this.roleAssignments.get(payload.targetSubjectId);
        if (!subjectRoles) {
          subjectRoles = new Map<string, Set<SystemRole>>();
          this.roleAssignments.set(payload.targetSubjectId, subjectRoles);
        }
        let scopeRoles = subjectRoles.get(scopeKey);
        if (!scopeRoles) {
          scopeRoles = new Set<SystemRole>();
          subjectRoles.set(scopeKey, scopeRoles);
        }
        scopeRoles.add(payload.role);
        break;
      }
      case 'UNASSIGN_ROLE': {
        if (!payload.role) return { ok: false, error: 'Role required for UNASSIGN_ROLE' };
        const subjectRoles = this.roleAssignments.get(payload.targetSubjectId);
        subjectRoles?.get(scopeKey)?.delete(payload.role);
        break;
      }
      case 'GRANT_PERMISSION': {
        if (!payload.permission) return { ok: false, error: 'Permission required for GRANT_PERMISSION' };
        this.grants.push({
          subjectId: payload.targetSubjectId,
          permission: payload.permission,
          scopeKey,
          effect: 'allow',
          expiresAt: payload.expiresAt,
          envelopeId,
        });
        break;
      }
      case 'REVOKE_PERMISSION': {
        if (!payload.permission) return { ok: false, error: 'Permission required for REVOKE_PERMISSION' };
        this.grants.push({
          subjectId: payload.targetSubjectId,
          permission: payload.permission,
          scopeKey,
          effect: 'deny',
          expiresAt: payload.expiresAt,
          envelopeId,
        });
        break;
      }
      case 'APPLY_RESTRICTION': {
        if (!payload.permission || !payload.restrictionId) {
          return { ok: false, error: 'Permission and restrictionId required for APPLY_RESTRICTION' };
        }
        this.restrictions.set(payload.restrictionId, {
          id: payload.restrictionId,
          meshId: payload.meshId,
          subjectId: payload.targetSubjectId,
          permission: payload.permission,
          scopeKey,
          status: 'active',
          createdAt: nowMs,
          expiresAt: payload.expiresAt,
          reason: payload.reason ?? 'Restriction applied',
          envelopeId,
        });
        break;
      }
      case 'LIFT_RESTRICTION': {
        if (!payload.restrictionId) return { ok: false, error: 'restrictionId required for LIFT_RESTRICTION' };
        const target = this.restrictions.get(payload.restrictionId);
        if (target && target.status === 'active') {
          target.status = 'lifted';
          target.resolvedAt = nowMs;
          target.resolvedBy = payload.actorId;
        }
        break;
      }
      case 'OVERRIDE_RESTRICTION': {
        if (!payload.restrictionId) return { ok: false, error: 'restrictionId required for OVERRIDE_RESTRICTION' };
        const target = this.restrictions.get(payload.restrictionId);
        if (target && target.status === 'active') {
          target.status = 'overridden';
          target.resolvedAt = nowMs;
          target.resolvedBy = payload.actorId;
        }
        break;
      }
    }

    // 8. Commit Audit & Replay Entries
    this.replayRegistry.set(replayKey, {
      meshId: payload.meshId,
      actorId: payload.actorId,
      keyId: payload.keyId,
      nonce: payload.nonce,
      expiresAt: payload.expiresAt,
      acceptedAt: nowMs,
    });

    this.envelopes.push(envelope);
    this.policyVersion++;

    // 9. Notify Listeners (Purge Evaluator Cache)
    for (const listener of this.commitListeners) {
      listener(this.policyVersion);
    }

    return { ok: true, policyVersion: this.policyVersion };
  }

  getAssignedRoles(subjectId: string, scopeKey: string): ReadonlySet<SystemRole> {
    const emptySet = new Set<SystemRole>();
    const subjectMap = this.roleAssignments.get(subjectId);
    if (!subjectMap) return emptySet;

    const result = new Set<SystemRole>();
    const systemRoles = subjectMap.get('system');
    if (systemRoles) {
      for (const r of systemRoles) result.add(r);
    }
    if (scopeKey !== 'system') {
      const scopeRoles = subjectMap.get(scopeKey);
      if (scopeRoles) {
        for (const r of scopeRoles) result.add(r);
      }
    }
    return result;
  }

  findActiveRestriction(
    subjectId: string,
    permission: string,
    scopeKey: string,
    nowMs: number
  ): AccessRestriction | null {
    for (const restriction of this.restrictions.values()) {
      if (
        restriction.status === 'active' &&
        restriction.subjectId === subjectId &&
        nowMs <= restriction.expiresAt &&
        (restriction.permission === permission || restriction.permission === '*') &&
        (restriction.scopeKey === 'system' || restriction.scopeKey === scopeKey)
      ) {
        return restriction;
      }
    }
    return null;
  }

  findDirectGrant(
    subjectId: string,
    permission: string,
    scopeKey: string,
    nowMs: number
  ): PermissionGrant | null {
    for (let i = this.grants.length - 1; i >= 0; i--) {
      const grant = this.grants[i];
      if (
        grant.subjectId === subjectId &&
        nowMs <= grant.expiresAt &&
        (grant.permission === permission || grant.permission === '*') &&
        (grant.scopeKey === 'system' || grant.scopeKey === scopeKey)
      ) {
        return grant;
      }
    }
    return null;
  }

  getAuditTrail(): ReadonlyArray<ControlEnvelope<SignedControlPayload>> {
    return this.envelopes;
  }

  getRestrictions(): ReadonlyArray<AccessRestriction> {
    return Array.from(this.restrictions.values());
  }
}
